import PSA from '../psa';
import FileDropHandler from '../dnd/FileDropHandler';
import ScrMen from '../widgets/scrmen/ScrMen';
import Utils from '../utils/Utils';
import Validator from '../utils/Validator';
import Logging from '../utils/logging';

const ClientDocument = rwt.widgets.base.ClientDocument;

/**
 * ConMgr - HTTP connection manager class
 */
export default class ConMgr extends FileDropHandler {

	/**
	 * constructs a new instance
	 * @param {CliCbkWdg} cbw callback widget
	 */
	constructor(cbw) {
		super('ssn.ConMgr');
		ConMgr.instance = this;
		this.iniDon = false;
		this.cbkWdg = null;
		this.rwtCon = null;
		this.bscMgr = null;
		this.ignRqu = false;
		this.rquCnt = 0;
		this.logCnt = 0;
		this.pingTmh = null;
		this.onSend = null;
		this.onRender = null;
		this._lastMessage = null;
		this._shutDownDone = false;
		this._shutDownReason = null;
		this._implicitShutDown = false;
		this._cid = null;
		this._ids = null;
		this._init(cbw);
	}

	/**
	 * @returns {ConMgr|undefined} the connection manager manager instance
	 */
	static getInstance() {
		return this.instance;
	}

	/**
	 * @returns {String} the connection ID
	 */
	get cid() {
		return this._cid;
	}

	/**
	 * @returns {String} the instance ID string
	 */
	get ids() {
		return this._ids;
	}

	/**
	 * @returns {Boolean} true if the session is already shut down
	 */
	get isShutDown() {
		return this._shutDownDone;
	}

	/**
	 * called on logout to clean-up
	 */	
	destroy() {
		this.cbkWdg = null;
		this.bscMgr = null;
		if ( this.pingTmh ) {
			const tmh = this.pingTmh;
			this.pingTmh = null;
			window.clearInterval(tmh);
		}
		if ( this.onSend ) {
			rap.off('send', this.onSend);
		}
		if ( this.onRender ) {
			rap.off('render', this.onRender);
		}
		delete this.onSend;
		delete this.onRender;
		if ( this.rwtCon ) {
			let con = this.rwtCon;
			if ( typeof con._org_retry === 'function' ) {
				con._retry = con._org_retry;
				delete con._org_retry;
			}
			// we do *not* restore the original error handler but set a dummy instead...
			con._handleError = function(evt) {
				// not nothing here - we're logged out...
			};
			if ( typeof con._org_handleError === 'function' ) {
				delete con._org_handleError;
			}
			this.rwtCon = null;
			this.iniDon = false;
		}
		// restore RAP's mouse cursor handling
		let cli = ClientDocument.getInstance();
		if ( cli && (typeof cli._org_applyGlobalCursor === 'function') ) {
			cli._applyGlobalCursor = cli._org_applyGlobalCursor;
			delete cli._org_applyGlobalCursor;
		}
		super.destroy();
	}

	/**
	 * shuts the session down
	 * @param {Function|null} callback optional callback function
	 */
	shutdown(callback) {
		this._implicitShutDown = false;
		this._doSndShtDwn(this.cid, 'shutdown', callback);
	}

	/**
	 * initializes client-side block screen management
	 * @param {Object} args arguments sent by the web server; may specify a block screen timeout (optional)
	 */
	iniBlkScr(args) {
		if ( this.iniDon ) {
			return;
		}
		this.onSend = Utils.bind(this, this._onSend);
		rap.on('send', this.onSend);
		this.onRender = Utils.bind(this, this._onRender);
		rap.on('render', this.onRender);
		const self = this;
		const con = this.rwtCon;
		if ( con ) {
			const hdl_err = con._handleError;
			if ( typeof hdl_err === 'function' ) {
				con._org_handleError = hdl_err;
				con._handleError = function(evt) {
					try {
						if ( self._isDbgLog() ) {
							const rcn = self._getRquCnt();
							self.trace("ERR #" + rcn);
						}
						con._org_handleError(evt);
					} finally {
						self._showBsc(false);
					}
				};
				// setup extended error handling (JS and JSON errors)
				this._iniXtrErrHdl(!!args.advJSLogging);
				// disable any mouse cursor handling by RAP
				const cli = ClientDocument.getInstance();
				if ( cli && (typeof cli._applyGlobalCursor === 'function') ) {
					// force a hard reset
					cli.setGlobalCursor("default");
					cli.setGlobalCursor(null);
					const fnc = cli._applyGlobalCursor;
					cli._org_applyGlobalCursor = fnc;
					cli._applyGlobalCursor = function(value) {
						if ( 'progress' === value || 'wait' === value ) {
							// suppress these cursor shapes
							value = null;
						}
						cli._org_applyGlobalCursor.call(cli, value);
					};
				}
				this.bscMgr = this.cbkWdg.bscMgr;
				this.bscMgr.setBscTmo(args.tos || 0, args.too || 0);
			}
			this.iniDon = true;
		}
	}

	/**
	 * sets the "ignore next request" flag
	 */
	setIgnRqu() {
		this._logRqu('NEXT IGNR');
		this.ignRqu = true;
	}

	/**
	 * initializes "alive ping" timer
	 */
	iniAlvPing(args) {
		const tmo = args.tmo || 0;
		const sck = args.sck || false;
		const self = this;
		if ( (tmo > 0) && !this.pingTmh ) {
			this.pingTmh = window.setInterval(() => {
				self._sndAlvPing(false);
			}, tmo);
		}
		if ( sck ) {
			self._sndAlvPing(true);
		}
	}

	/**
	 * initializes JS logging
	 * @param {*} parameter object
	 */
	initJSLogging(args) {
		// forward this to logging handler
		Logging.getInstance().applyConfiguration(args);
	}

	/**
	 * FileDropHandler: handles a file drop
	 * @see FileDropHandler
	 * @param {File[]} files array of dropped files
	 * @override
	 */
	handleDrop(files) {
		// in fact, we have nothing special to do so far :-)
	}

	/**
	 * FileDropHandler: shows a (warning) message
	 * @see FileDropHandler
	 * @param {String} msg message text
	 * @override
	 */
	showMessage(msg) {
		if ( msg ) {
			this.warn(msg);
		}
	}

	/**
	 * sends a "timer hit" notification
	 * @param {String} name timer name
	 */
	sendTimerHit(name) {
		if ( Validator.isString(name) ) {
			this._sendTimerHit(name);
		}
	}

	/**
	 * first time initialization
	 * @param {CliCbkWdg} cbw callback widget
	 */
	_init(cbw) {
		this.cbkWdg = cbw;
		const self = this;
		this._iniBasErrHdl();
		const con = rwt.remote.Connection.getInstance();
		if ( con ) {
			const cid = con._connectionId;
			const ids = cbw.instance_id;
			if ( Validator.isString(ids) ) {
				if ( typeof con._createRequest === 'function' ) {
					// hook-in our oww request creation function
					con._createRequest = function() {
						return self._createRegularRAPRequest(con, cid, ids);
					}
				}
				const psh = rwt.client.ServerPush.getInstance();
				if ( psh && (typeof psh._createRequest === 'function') ) {
					// hook-in our oww request creation function
					psh._createRequest = function() {
						return self._createServerPushRAPRequest(psh, con, cid, ids);
					}
				}
			}
			this._cid = cid;
			this._ids = Validator.isString(ids) ? ids : cid;
			const rtf = con._retry;
			if ( typeof rtf === 'function' ) {
				// ok, hook in
				this.rwtCon = con;
				con._org_retry = rtf;
				con._retry = function() {
					con._org_retry();
					self._nfyConErr();
				};
			}
			// hook-in menu item execution
			let mni_pty = rwt.widgets.MenuItem.prototype;
			let exe_fnc = mni_pty.execute;
			if ( typeof exe_fnc === 'function' ) {
				mni_pty._org_execute = exe_fnc;
				let hook_exe_fnc = function(args) {
					// trigger original method...
					exe_fnc.apply(this, arguments);
					// ... and make sure that every menu is closed right now
					ScrMen.closeAllMenus();

				}
				mni_pty.execute = hook_exe_fnc;
			}
		} else {
			throw new Error("Cannot go on without RAP connection instance!");
		}
		// update current display
		const dsp = rwt.widgets.Display.getCurrent();
		if ( dsp ) {
			// hook-in shutdown handler
			const cid = this.cid;
			if ( Validator.isString(cid) ) {
				const _snd_sht = dsp._sendShutdown;
				if ( typeof _snd_sht === 'function' ) {
					dsp._org_sendShutdown = _snd_sht;
					dsp._sendShutdown = function() {
						self._doSndShtDwn(cid, 'unload', null);
					}
				}
				// always add our own "pagehide" event handler
				window.addEventListener('pagehide', (e) => {
					self._doHandlePageHide(dsp, cid);
				});
			} else {
				this.warn("No connection ID available!");
			}
			// hook-in focus control
			const _set_foc = dsp.setFocusControl;
			if ( typeof _set_foc === 'function' ) {
				dsp._org_set_foc = _set_foc;
				dsp.setFocusControl = function(idw) {
					try {
						dsp._org_set_foc(idw);
					}
					catch ( e ) {
						self.warn('Failed to set the focus to widget "' + idw + '"!', e);
					}
				}
			}
		}
		// hook-in mouse button work around
		const abp = rwt.remote.EventUtil.addButtonToProperties;
		if ( typeof abp === 'function' ) {
			const wa_abp = function(properties, event) {
				// call original function
				abp(properties, event);
				// make sure that the property "button" is present and an integer
				const btn = properties.button | 0;
				properties.button = btn;
			};
			rwt.remote.EventUtil.addButtonToProperties = wa_abp;
		}
		// hook-in rwt.widgets.base.MultiCellWidget._isImageCell() work around
		const mcw = rwt.widgets.base.MultiCellWidget.prototype;
		if ( mcw ) {
			// the original code is
			// _isImageCell : function( cell ) {
			// 	var type = this.__cellData[ cell ][ 0 ];
			// 	return type == "image";
			// },
			const is_image_cell_wa = function(ci) {
				let ok = false;
				let err = null;
				try {
					const cell_data = this.__cellData;
					if ( cell_data ) {
						const cell = cell_data[ci];
						if ( cell ) {
							const type = cell[0];
							ok = true;
							return 'image' === type;
						}
					}
				} catch ( e ) {
					ok = false;
					err = e;
				}
				if ( !ok ) {
					if ( !err ) {
						err = 'Cannot read cell data.';
					}
					self.warn('Caught an error in rwt.widgets.base.MultiCellWidget._isImageCell():', err);
				}
				return false;
			};
			mcw._isImageCell = is_image_cell_wa;
		}
	}

	/**
	 * creates an URL parameter string
	 * @param {String} url the base URL
	 * @param {String} cid the RAP connection ID
	 * @param {String} ids web client instance ID
	 * @returns {String} the URL parameter string
	 */
	_createUrlParam(url, cid, ids) {
		return (url.indexOf("?") === -1 ? "?cid=" : "&cid=") + cid + "&wsvids=" + encodeURIComponent(ids);
	}

	/**
	 * creates a regular RAP request object
	 * @param {*} con the RAP connection instance
	 * @param {String} cid the RAP connection ID
	 * @param {String} ids web client instance ID
	 * @returns {*} the created RAP request object
	 */
	_createRegularRAPRequest(con, cid, ids) {
		const url = '' + con._url;
		const param = this._createUrlParam(url, cid, ids);
		const request = new rwt.remote.Request(url + param, "POST", "application/json");
		request.setSuccessHandler( con._handleSuccess, con );
		request.setRedirectHandler( con._handleRedirect, con );
		request.setErrorHandler( con._handleError, con );
		return request
	}

	/**
	 * creates a "server push" RAP request object
	 * @param {*} psh the RAP server push handler
	 * @param {*} con the RAP connection instance
	 * @param {String} cid the RAP connection ID
	 * @param {String} ids web client instance ID
	 * @returns {*} the created RAP request object
	 */
	_createServerPushRAPRequest(psh, con, cid, ids) {
		const url = '' + con._url;
		const request = new rwt.remote.Request(url, "GET", "application/javascript");
		request.setSuccessHandler( psh._handleSuccess, psh );
		request.setRedirectHandler( psh._handleRedirect, psh );
		request.setErrorHandler( psh._handleError, psh );
		const data = "servicehandler=org.eclipse.rap.serverpush&cid=" + cid + "&wsvids=" + encodeURIComponent(ids);
		request.setData(data);
		return request

	}

	/**
	 * sends shutdown notification
	 * @param {String} cid connection / session identifier
	 * @param {String} reason  shutdown reason
	 * @param {Function|null} callback optional callback function
	 */
	_doSndShtDwn(cid, reason, callback) {
		if ( this.isShutDown ) {
			return;
		}
		const eff_reason = this._shutDownReason || reason;
		const implicit = !!this._implicitShutDown;
		console.log(`Initiating session shutdown. Reason: "${eff_reason}"; implicit="${implicit}"`);
		// mark the instance as "shut down done"
		this._shutDownDone = true;
		// destroy the wake lock (if set)
		PSA.getInst().getWakeLock().destroy();

		// create the body object
		const body = window.btoa(JSON.stringify({ cid: cid, ids: this.ids, shutdown: true, reason: eff_reason }));
		// construct target URL - we MUST use our own servlet for this, because the default RAP servlet requires JSON data
		const target = `psashtdwn?cid=${cid}&wsvids=${encodeURIComponent(this.ids)}&shutdown=true&reason=${eff_reason}`; 
		// fetch options
		const options = {
			method: 'POST',
			keepalive: true,
			headers: {
				'Content-Type': 'text/plain;charset=UTF-8',
			},
			body: body
		};
		console.debug(`Sending shutdown request to URL "${target}" using options:`, options);
		window.fetch(target, options)
			.then(response => {
				const status = response.status;
				if ( status >= 200 && status <= 400 ) {
					console.log('Shutdown completed.');
				} else {
					console.warn('Error on shutdown request:', status);
				}
				return response.text();
			})
			.then(text => {
				console.log('Shutdown response:', text);
			})
			.catch(error => {
				if ( !implicit ) {
					// complain only, if we're doing an explicit shutdown - 
					// if the user just navigates away, then the browser will cancel the request, which then will invoke this error handler
					console.warn('Error sending the shutdown request!', error);
				}
			})
			.finally(() => {
				if ( typeof callback === 'function' ) {
					console.log('Invoking callback after the shutdown request was sent');
					callback();
				}
			});
	}

	/**
	 * handles "pagehide" events
	 * @param {*} dsp the RAP display instance
	 * @param {String} cid connection / session identifier
	 */
	_doHandlePageHide(dsp, cid) {
		if ( this.isShutDown ) {
			console.log('Session already shut down. Nothing more to do.');
			return;
		}
		this._shutDownReason = 'pagehide';
		this._implicitShutDown = true;
		if ( dsp && typeof dsp._onUnload === 'function' ) {
			// go the regular way and let RAP know about this
			console.log('Triggering unload processing due to a "pagehide" event.');
			dsp._onUnload();
		} else {
			// do it directly
			console.log('Direct session shutdown due to "pagehide" event.');
			this._doSndShtDwn(cid, 'pagehide', null);
		}
	}

	/**
	 * sends an "alive ping" request
	 * @param {Boolean} sck "set cookie" flag
	 */
	_sndAlvPing(sck) {
		const cid = this.cid;
		const ids = this.ids;
		const rqu =  new XMLHttpRequest();
		const par = `?cid=${cid}&wsvids=${encodeURIComponent(ids)}&alvping=true&ssncookie=${(sck ? 'true' : 'false')}`;
		const url = 'psashtdwn' + par;
		const data = {
			cid: cid,
			ids: ids,
			alvping: true,
			sck: sck || false,
			tms: Date.now()
		};
		rqu.open("POST", url, true);
		rqu.setRequestHeader('Content-Type', 'text/plain; charset=UTF-8');
		const self = this;
		rqu.onreadystatechange = function() {
			self._onRquDone(rqu);
		};
		rqu.onerror = function() {
			self._onRquError(rqu);
		};		
		rqu.send(JSON.stringify(data));
	}

	/**
	 * sends a "timer hit" request
	 * @param {String} name timer name
	 */
	_sendTimerHit(name) {
		const cid = this.cid;
		const ids = this.ids;
		const rqu =  new XMLHttpRequest();
		const tname = encodeURIComponent(name);
		const par = `?cid=${cid}&wsvids=${encodeURIComponent(ids)}&timerhit=true&tname=${tname}`;
		const url = 'psashtdwn' + par;
		const data = {
			cid: cid,
			ids: ids,
			timerhit: true,
			tname: tname,
			tms: Date.now()
		};
		rqu.open("POST", url, true);
		rqu.setRequestHeader('Content-Type', 'text/plain; charset=UTF-8');
		const self = this;
		rqu.onreadystatechange = function() {
			self._onRquDone(rqu);
		};
		rqu.onerror = function() {
			self._onRquError(rqu);
		};
		rqu.send(JSON.stringify(data));
	}

	/**
	 * handles XHR ready state changes
	 * @param {XMLHttpRequest} rqu the XHR request
	 */
	_onRquDone(rqu) {
		if ( rqu.readyState === 4 ) {
			rqu.onreadystatechange = null;
			rqu.abort();
		}
	}
	
	/**
	 * handles XHR errors
	 * @param {XMLHttpRequest} rqu the XHR request
	 */
	_onRquError(rqu) {
		this.warn(`Error in XHR request! Code=${rqu.status}.`);
		this.warn(`Error message: "${rqu.statusText}".`);
	}

	_nfyConErr() {
		if ( this.cbkWdg ) {
			let par = {};
			par.conRst = true;
			this.cbkWdg.nfyGlb("conRst", par, true);
		}
	}
	
	_isDbgLog() {
		return this.isTraceEnabled();
	}

	_incRquCnt() {
		return ++this.rquCnt;
	}
	
	_getRquCnt() {
		return this.rquCnt;
	}

	_logRqu(txt) {
		if ( this._isDbgLog() ) {
			const cnt = ++this.logCnt;
			const now = new Date();
			this.trace(now.toISOString() + ' - CON #' + cnt + ': ' + txt);
		}
	}

	/**
	 * checks the pending messages that the client connection is about to send
	 * @returns {Boolean} true if a block screen should be triggered; false otherwise
	 */
	_chkSndMsg() {
		let show = false;
		if ( this.rwtCon ) {
			const con = this.rwtCon;
			const opa = con._writer._operations;
			if ( opa && (opa.length > 0) ) {
				// check all operations sent to the web server
				const	cnt = opa.length;
				for ( let i=0 ; !show && (i < cnt) ; ++i ) {
					const opr = opa[i];
					const code = opr[0] || '';			// main code 'set' | 'notify' | 'call' is at index 0
					// we ignore 'set' operations at all - they may not trigger complex operations
					if ( (code === 'notify') || (code === 'call') ) {
						const what = opr[2] || '';		// specific operation code is at index 2
						// we ignore 'FocusIn', 'FocusOut' and 'PSA_NFY_NBS' notifications; everything else is considered to be "block screen" relevant
						show = !((what === 'FocusIn') || (what === 'FocusOut') || (what === 'PSA_NFY_NBS'));
					}
				}
			}
		}
		return show;
	}

	_onSend() {
		this._logRqu('SEND');
		if ( this._chkSndMsg() ) {
			this._showBsc(true);
		} else {
			this._logRqu('IRRELEVANT - block screen request ignored; no relevant messages found.')
		}
	}

	_onRender() {
		this._logRqu('RNDR');
		this._showBsc(false);
	}

	/**
	 * triggers a block screen request, either "show" or "hide"
	 * @param {Boolean} sbc flag whether to trigger a show or hide request of the block screen
	 */
	_showBsc(sbc) {
		let ign = !!this.ignRqu;
		if ( ign ) {
			this._logRqu('IGNR: ' + (sbc ? 'ON' : 'OFF'));
		}
		if ( ign && !sbc ) {
			// reset the "ignore" flag
			this.ignRqu = false;
			if ( this.bscMgr ) {
				ign = !this.bscMgr.isBscVis();
			}
		}
		if ( !ign && this.bscMgr ) {
			this.bscMgr.show(sbc);
		}
	}

	/**
	 * initializes basic error handling
	 */
	_iniBasErrHdl() {
		const self = this;
		if ( typeof rwt.runtime.ErrorHandler.showErrorBox === 'function' ) {
			const erh = rwt.runtime.ErrorHandler;
			erh._org_showErrorBox = rwt.runtime.ErrorHandler.showErrorBox;
			rwt.runtime.ErrorHandler.showErrorBox = function(errorType, freeze, errorDetails) {
				let bsc = self.bscMgr;
				if ( !bsc && self.cbkWdg ) {
					bsc = self.cbkWdg.bscMgr;
				}
				if ( bsc ) {
					bsc.frcOff();
				}
				erh._org_showErrorBox(errorType, freeze, errorDetails);
			}
		}
	}

	/**
	 * initializes extended error handling
	 * @param {Boolean} advanced if true then the advanced JS logging is activated
	 */
	_iniXtrErrHdl(advanced) {
		const self = this;
		const psa = PSA.getInst();
		const psa_err_hdl = function(err, opr) {
			if ( psa.cliCbkWdg ) {
				// ok, *WE* handle this in order to terminate the session gracefully
				const par = {};
				if ( err ) {
					par.info = err.toString();
					if ( err.stack ) {
						par.stack = err.stack;
					}
				} else {
					par.info = 'jsCrash';
				}
				if ( opr !== undefined ) {
					par.operation = JSON.stringify(opr);
				}
				if ( self._lastMessage !== null ) {
					par.lastMessage = self._lastMessage;
				}
				psa.cliCbkWdg.nfyGlb('jsCrash', par, false);
				if ( self.bscMgr ) {
					self.bscMgr.frcOff();
				}
				return true;
			}
			else {
				return false;
			}
		};
		const mpr = rwt.remote.MessageProcessor;
		if ( advanced ) {
			// processMessage : function( messageObject, callback, startOffset ) {}
			if ( typeof rwt.remote.MessageProcessor.processMessage === 'function' ) {
				mpr._org_processMessage = mpr.processMessage;
				mpr.processMessage = function(messageObject, callback, startOffset) {
					self._lastMessage = JSON.stringify(messageObject);
					mpr._org_processMessage(messageObject, callback, startOffset);
				}
			}
			// _processSet : function( targetId, properties ) {}
			if ( typeof rwt.remote.MessageProcessor._processSet === 'function' ) {
				mpr._org_processSet = mpr._processSet;
				mpr._processSet = function(targetId, properties) {
					try {
						mpr._org_processSet(targetId, properties);
					} catch ( err ) {
						self.warn(`Failed to set properties ${JSON.stringify(properties)} on the target object with the id "${targetId}" !`);
						self.warn(err);
					}
				}
			}
			// _processSetImpl : function( targetObject, handler, properties ) {}
			if ( typeof rwt.remote.MessageProcessor._processSetImpl === 'function' ) {
				mpr._org_processSetImpl = mpr._processSetImpl;
				mpr._processSetImpl = function(targetObject, handler, properties) {
					if ( targetObject ) {
						try {
							mpr._org_processSetImpl(targetObject, handler, properties);
						} catch ( err ) {
							self.warn(`Failed to set properties ${JSON.stringify(properties)} on the target object`, targetObject);
							self.warn(err);
						}
					} else {
						self.warn(`Cannot set properties ${JSON.stringify(properties)} because the target object is not available!`);
					}
				}
			}
		}
		// _processError : function( error, operation ) {}
		if ( typeof rwt.remote.MessageProcessor._processError === 'function' ) {
			mpr._org_processError = mpr._processError;
			mpr._processError = function(err, opr) {
				if ( !psa_err_hdl(err, opr) ) {
					// we cannot handle this by ourself
					mpr._org_processError(err, opr);
				}
			};
		}
		// processJavaScriptError : function( error ) {}
		const erh = rwt.runtime.ErrorHandler;
		if ( typeof rwt.runtime.ErrorHandler.processJavaScriptError === 'function' ) {
			erh._org_processJavaScriptError = erh.processJavaScriptError;
			erh.processJavaScriptError = function(err) {
				if ( !psa_err_hdl(err) ) {
					// we cannot handle this by ourself
					erh._org_processJavaScriptError(err);
				}
			};
		}
		// processJavaScriptErrorInResponse : function( script, error, currentRequest ) {}
		if ( typeof rwt.runtime.ErrorHandler.processJavaScriptErrorInResponse === 'function' ) {
			erh._org_processJavaScriptErrorInResponse = erh.processJavaScriptErrorInResponse;
			erh.processJavaScriptErrorInResponse = function( script, error, currentRequest ) {
				if ( !psa_err_hdl(error, currentRequest) ) {
					// we cannot handle this by ourself
					erh._org_processJavaScriptErrorInResponse(script, error, currentRequest);
				}
			};
		}
		const dnd_hdl = rwt.event.DragAndDropHandler.getInstance();
		if ( dnd_hdl && (typeof dnd_hdl._renderCursor === 'function') ) {
			dnd_hdl._org_renderCursor = dnd_hdl._renderCursor;
			dnd_hdl._renderCursor = function() {
				try {
					dnd_hdl._org_renderCursor();
				}
				catch ( err ) {
					/* *gulp* */
					psa.Log.logErr('Error while drag&drop suppressed!', true);
				}
			}
		}
		if ( typeof rwt.widgets.util.GridCellToolTipSupport.getCurrentToolTipTargetBounds === 'function' ) {
			const grc_ttp = rwt.widgets.util.GridCellToolTipSupport;
			grc_ttp._org_getCurrentToolTipTargetBounds = grc_ttp.getCurrentToolTipTargetBounds;
			grc_ttp.getCurrentToolTipTargetBounds = function(row) {
				try {
					if ( row ) {
						return grc_ttp._org_getCurrentToolTipTargetBounds(row);
					}
				} catch ( e ) {
					/* *gulp* */
					psa.Log.logErr('Error in grid tooltip support suppressed!', true);
				}
				return {
					left : 0,
					top : 0,
					height : 0,
					width : 0
				};
			}
		}
		const mnu_mgr = rwt.widgets.util.MenuManager.getInstance();
		if ( mnu_mgr && (typeof mnu_mgr.update === 'function') ) {
			mnu_mgr._org_update = mnu_mgr.update;
			mnu_mgr.update = function( target, eventType ) {
				try {
					mnu_mgr._org_update(target, eventType);
				} catch ( e ) {
					/* *gulp* */
					psa.Log.logErr('Error in MenuManager.update() suppressed!', true);
				}
			};
		}
		if ( typeof rwt.event.MouseEvent.prototype.getModifierState === 'undefined' ) {
			// just add this to simulate a standard mouse event
			rwt.event.MouseEvent.prototype.getModifierState = function(what) {
				// there's not much that we could do here
				return false;
			}
		}
		// hook in an own implementation of rwt.event.MouseEvent.prototype.getDomEvent
		const _org_gde = rwt.event.MouseEvent.prototype.getDomEvent;
		if ( typeof _org_gde === 'function' ) {
			rwt.event.MouseEvent.prototype.getDomEvent = function() {
				try {
					const de = _org_gde.apply(this);
					if ( de && (typeof de.getModifierState === 'undefined') ) {
						// the dummy event object must provide the method getModifierState()!
						de.getModifierState = function(what) {
							return false;
						}
					}
					return de;
				} catch ( err ) {
					console.warn(err);
					return null;
				}
			}
		}
		// with the code above we should not need this
		// if ( typeof rwt.event.DomEvent.prototype.isAltGrPressed === 'function' ) {
		// 	const _org_method = rwt.event.DomEvent.prototype.isAltGrPressed;
		// 	rwt.event.DomEvent.prototype.isAltGrPressed = function() {
		// 		try {
		// 			return _org_method.apply(this);
		// 		} catch ( err ) {
		// 			console.warn(err);
		// 			return false;
		// 		}
		// 	}
		// }
	}
}						
